import { ICoreContext, IRequestContext } from "@msdyn365-commerce/core";
import { AttributeValue, Customer, CustomerAttribute } from "@msdyn365-commerce/retail-proxy";
import { IProductAgreement } from "./types";
import { IAppSettings } from "../settings/app.settings.autogenerated";

function getAppConfig(context: ICoreContext | IRequestContext): IAppSettings | undefined {
    const request = 'request' in context ? context.request : context;
    return request.app.config as IAppSettings | undefined;
}

function getAgreementAttributeRecordIds(appConfig: IAppSettings | undefined): number[] | undefined {
    const recordIds = appConfig?.agreementProductAttributeRecordId
        ?.split(',')
        .map(Number)
        .filter(Boolean);

    return recordIds;
}

type AttributeValueOrCustomerAttribute = AttributeValue | CustomerAttribute;

function getHKjottavtaleProductAgreement(appConfig: IAppSettings | undefined, from: 'customer' | 'product', attributes: AttributeValueOrCustomerAttribute[] | undefined): IProductAgreement | undefined {
    const recordId = Number(appConfig?.hkjoottavtaleAttributeRecordId);
    if (attributes === undefined || !Number.isFinite(recordId)) {
        return undefined;
    }

    const attribute = attributes.find(attr => attr.RecordId === recordId);
    if (!attribute) {
        return undefined;
    }

    if (from === 'customer' && 'AttributeValue' in attribute && attribute.AttributeValue?.IntegerValue) {
        return {
            AttributeRecordId: attribute.RecordId!,
            Name: attribute.Name!
        };
    }

    // from === 'product'
    if ('IntegerValue' in attribute && typeof attribute.IntegerValue === 'number' && attribute.IntegerValue >= 2) {
        return {
            AttributeRecordId: attribute.RecordId!,
            Name: attribute.Name!
        };
    }

    return undefined;
}

export function getAgreementProductRefinersToHide(context: ICoreContext, customer: Customer | undefined): number[] | undefined {
    const refinerIds = getAgreementAttributeRecordIds(getAppConfig(context));

    // Hide all refiners if customer is not available
    if (!customer) {
        return refinerIds;
    }

    const refinersToHide = refinerIds?.filter(refinerId => {
        const attribute = customer.Attributes?.find(x => x.RecordId === refinerId);
        return !attribute?.AttributeValue?.BooleanValue && !attribute?.AttributeValue?.IntegerValue;
    });

    return refinersToHide;
}

export function getProductAgreements(context: ICoreContext | IRequestContext, attributes: AttributeValue[] | undefined): IProductAgreement[] | undefined {
    const appConfig = getAppConfig(context);
    const checkAgreements = getAgreementAttributeRecordIds(appConfig);

    if (!attributes || !checkAgreements) {
        return undefined;
    }

    const agreements: IProductAgreement[] = checkAgreements
        .map(id => attributes.find(attr => attr.RecordId === id))
        .filter(x => x && (x.BooleanValue || x.IntegerValue))
        .map(x => ({
            AttributeRecordId: x?.RecordId,
            Name: x?.Name
        }) as IProductAgreement);

    const hkjoottavtaleAttr = getHKjottavtaleProductAgreement(appConfig, 'product', attributes);
    if (hkjoottavtaleAttr) {
        agreements.push(hkjoottavtaleAttr);
    }

    return agreements;
}

export function getCustomerAgreements(context: ICoreContext | IRequestContext, attributes: CustomerAttribute[] | undefined): IProductAgreement[] | undefined {
    const appConfig = getAppConfig(context);
    const checkAgreements = getAgreementAttributeRecordIds(appConfig);

    if (!attributes || !checkAgreements) {
        return undefined;
    }

    // Find the attributes that match the configured agreement IDs and should be checked for truth
    const findAttributes = checkAgreements
        .map(id => attributes.find(attr => attr.RecordId === id))
        .filter(Boolean) as CustomerAttribute[];

    const agreements: IProductAgreement[] = findAttributes
        // Include only attributes that are Boolean true or Integer with a value greater than 0
        .filter(x => x.AttributeValue?.BooleanValue || x.AttributeValue?.IntegerValue)
        .map(x => ({
            AttributeRecordId: x?.RecordId,
            Name: x?.Name
        }) as IProductAgreement
    );

    const hkjoottavtaleAttr = getHKjottavtaleProductAgreement(appConfig, 'customer', attributes);
    if (hkjoottavtaleAttr) {
        agreements.push(hkjoottavtaleAttr);
    }

    return agreements;
}